import { Group, Text } from "@mantine/core";
import { Money } from "@/components/Money";
import { Mono, ExternalMono } from "@/components/Mono";
import { Bps } from "@/components/Money";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { AdminActionButton } from "@/components/AdminActionButton";
import { humanize, formatMicroUsdc } from "@/lib/format";
import { txUrl, baseTxUrl, baseAddressUrl } from "@/lib/explorer";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  ReserveTopUpLegDoc,
  SettlementDoc,
  SettlementObligationDoc,
  WithDocId,
} from "@/firestore/types";
import type { EntityConfig } from "../types";
import { rescheduleSettlement } from "@/api/actions";
import { monoCol, moneyCol, relatedCol, stateCol, textCol, timeCol } from "../shared";

type P<T> = WithDocId<T>;

// ─────────────────────────── Settlement Obligation ───────────────────────────

export const settlementObligationConfig: EntityConfig<SettlementObligationDoc> = {
  key: "settlementObligation",
  collection: COLLECTIONS.settlementObligations,
  title: "Settlement Obligation",
  pluralTitle: "Settlement Obligations",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<SettlementObligationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<SettlementObligationDoc>>((r) => r.state),
    relatedCol<P<SettlementObligationDoc>>("payment", "Payment", "payment", (r) => r.sourcePayment),
    moneyCol<P<SettlementObligationDoc>>("auth", "Authorized", (r) => r.authorizedAmountMicroUsdc),
    moneyCol<P<SettlementObligationDoc>>("final", "Final", (r) => r.finalAmountMicroUsdc),
    moneyCol<P<SettlementObligationDoc>>("reserve", "In reserve", (r) => r.amountInReserveMicroUsdc),
    moneyCol<P<SettlementObligationDoc>>("settled", "Settled", (r) => r.amountSettledMicroUsdc),
    moneyCol<P<SettlementObligationDoc>>("released", "Released", (r) => r.amountReleasedMicroUsdc),
    timeCol<P<SettlementObligationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => {
    const settled = d.amountSettledMicroUsdc ?? 0;
    const released = d.amountReleasedMicroUsdc ?? 0;
    const target = d.finalAmountMicroUsdc ?? d.authorizedAmountMicroUsdc;
    const remaining = target - settled;
    const reconciled = d.state === "settled" ? remaining <= 0 : true;
    // Live Reserve position: what moved in, minus what was burned to Rain and returned to Main.
    // A captured obligation settles once it equals the final amount; above it, the excess of a
    // reducing capture is being returned to Main; below it, top-up legs still owe Reserve.
    const net = d.amountInReserveMicroUsdc - settled - released;
    const position =
      d.state !== "captured" || d.finalAmountMicroUsdc == null
        ? null
        : net > d.finalAmountMicroUsdc
          ? { color: "orange", text: `${formatMicroUsdc(net - d.finalAmountMicroUsdc)} excess awaiting release to Main` }
          : net < d.finalAmountMicroUsdc
            ? { color: "orange", text: `${formatMicroUsdc(d.finalAmountMicroUsdc - net)} still to stage into Reserve` }
            : { color: "teal", text: "equals final · ready to settle" };
    return [
      { label: "Payment", value: <RelatedLink entity="payment" id={d.sourcePayment} /> },
      { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
      { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
      { label: "Authorized", value: <Money micro={d.authorizedAmountMicroUsdc} /> },
      { label: "Final", value: <Money micro={d.finalAmountMicroUsdc} fw={600} /> },
      { label: "In reserve", value: <Money micro={d.amountInReserveMicroUsdc} /> },
      { label: "Settled", value: <Money micro={d.amountSettledMicroUsdc} /> },
      { label: "Released to Main", value: <Money micro={d.amountReleasedMicroUsdc} /> },
      {
        label: "Reserve position",
        full: true,
        value: (
          <Text c={position?.color} size="sm">
            in reserve {formatMicroUsdc(d.amountInReserveMicroUsdc)} − settled {formatMicroUsdc(settled)} − released{" "}
            {formatMicroUsdc(released)} = {formatMicroUsdc(net)}
            {position ? ` · ${position.text}` : ""}
          </Text>
        ),
      },
      {
        label: "Conservation check",
        full: true,
        value: (
          <Text c={reconciled ? "teal" : "red"} size="sm">
            settled {formatMicroUsdc(settled)} of {formatMicroUsdc(target)} target ·{" "}
            {remaining <= 0 ? "fully settled" : `${formatMicroUsdc(remaining)} remaining`}
            {!reconciled ? " · ⚠ state=settled but amount unreconciled" : ""}
          </Text>
        ),
      },
    ];
  },
  related: (d) => [
    {
      title: "Settlement executions",
      entity: "settlement",
      collection: COLLECTIONS.settlements,
      fkField: "obligationId",
      value: d.id,
      orderBy: [["attempt", "asc"]],
    },
    {
      title: "Reserve top-up legs",
      entity: "reserveTopUpLeg",
      collection: COLLECTIONS.reserveTopUpLegs,
      fkField: "sourcePayment",
      value: d.sourcePayment,
      orderBy: [["legIndex", "asc"]],
    },
    {
      title: "Holds",
      entity: "hold",
      collection: COLLECTIONS.holds,
      fkField: "sourcePayment",
      value: d.sourcePayment,
    },
    {
      // Main → Reserve top-ups and the Reserve → Main releases (excess / refund) of this payment.
      title: "Smart-account operations",
      entity: "smartAccountOperation",
      collection: COLLECTIONS.smartAccountOperations,
      fkField: "sourcePayment",
      value: d.sourcePayment,
    },
  ],
  actions: (d) => <AdminActionButton action={rescheduleSettlement} params={{ paymentId: d.sourcePayment }} />,
};

// ─────────────────────────── Settlement (execution) ───────────────────────────

export const settlementConfig: EntityConfig<SettlementDoc> = {
  key: "settlement",
  collection: COLLECTIONS.settlements,
  title: "Settlement Execution",
  pluralTitle: "Settlement Executions",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<SettlementDoc>>("id", "ID", (r) => r.id),
    stateCol<P<SettlementDoc>>((r) => r.state),
    textCol<P<SettlementDoc>>("attempt", "Attempt", (r) => String(r.attempt)),
    moneyCol<P<SettlementDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    relatedCol<P<SettlementDoc>>("obligation", "Obligation", "settlementObligation", (r) => r.obligationId),
    monoCol<P<SettlementDoc>>("burn", "Burn tx", (r) => r.burnTxHash),
    monoCol<P<SettlementDoc>>("mint", "Mint tx", (r) => r.mintTxHash),
    timeCol<P<SettlementDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => (
    <Group gap="xs">
      <Mono value={d.id} />
      <Text size="sm" c="dimmed">
        attempt {d.attempt} · {humanize(d.mechanism ?? "cctp")}
      </Text>
    </Group>
  ),
  detailFields: (d) => [
    { label: "Obligation", value: <RelatedLink entity="settlementObligation" id={d.obligationId} /> },
    { label: "Attempt", value: String(d.attempt) },
    { label: "Mechanism", value: humanize(d.mechanism ?? "cctp") },
    { label: "Amount", value: <Money micro={d.amountMicroUsdc} fw={600} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Source SAO", value: <RelatedLink entity="smartAccountOperation" id={d.sourceSmartAccountOperation} /> },
    {
      label: "Source tx (burn, HyperEVM)",
      value: <ExternalMono value={d.burnTxHash} href={txUrl(d.burnTxHash)} />,
    },
    {
      label: "Destination tx (mint, Base)",
      value: <ExternalMono value={d.mintTxHash} href={baseTxUrl(d.mintTxHash)} />,
    },
    {
      label: "Destination (Base collateral)",
      value: <ExternalMono value={d.destinationAddressBase} href={baseAddressUrl(d.destinationAddressBase)} />,
    },
    { label: "Attestation", value: <Mono value={d.attestation} short copy /> },
  ],
};

// ─────────────────────────── Reserve Top-Up Leg ───────────────────────────

export const reserveTopUpLegConfig: EntityConfig<ReserveTopUpLegDoc> = {
  key: "reserveTopUpLeg",
  collection: COLLECTIONS.reserveTopUpLegs,
  title: "Reserve Top-Up Leg",
  pluralTitle: "Reserve Top-Up Legs",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<ReserveTopUpLegDoc>>("id", "ID", (r) => r.id),
    stateCol<P<ReserveTopUpLegDoc>>((r) => r.state),
    textCol<P<ReserveTopUpLegDoc>>("legIndex", "Leg", (r) => String(r.legIndex)),
    moneyCol<P<ReserveTopUpLegDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    relatedCol<P<ReserveTopUpLegDoc>>("payment", "Payment", "payment", (r) => r.sourcePayment),
    timeCol<P<ReserveTopUpLegDoc>>("fireAt", "Fire at", (r) => r.fireAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Leg index", value: String(d.legIndex) },
    { label: "Planned fraction", value: <Bps bps={d.plannedFractionBps} /> },
    { label: "Amount", value: <Money micro={d.amountMicroUsdc} /> },
    { label: "Payment", value: <RelatedLink entity="payment" id={d.sourcePayment} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
    { label: "Source SAO", value: <RelatedLink entity="smartAccountOperation" id={d.sourceSmartAccountOperation} /> },
    { label: "Fire at", value: <Timestamp value={d.fireAt} absolute /> },
  ],
};
